import { useQuery } from "@tanstack/react-query";
import { Target, Activity, TrendingUp, Calendar, Award, BarChart3 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import type { Goal, Activity as ActivityType, DashboardStats, CategoryStats, JournalStats, HabitStats, CalendarStats } from "@shared/schema";
import { useTodayDate } from "@/hooks/useTodayDate";
import { isGoalVisibleOnMainSurfaces } from "@/lib/goal-visibility";

interface WidgetSettings {
  showProgress?: boolean;
  showStreak?: boolean;
  showDetails?: boolean;
  maxItems?: number;
  metric?: string;
}

export function GoalsOverviewWidget({ showProgress = true, showDetails = true }: WidgetSettings) {
  const { data: allGoals = [] } = useQuery<Goal[]>({
    queryKey: ["/api/goals"],
  });
  const goals = allGoals.filter(isGoalVisibleOnMainSurfaces);

  const goalTrends = {
    totalGoals: goals.length,
    activeGoals: goals.filter(g => g.status === "active").length,
    completedGoals: goals.filter(g => g.status === "completed").length,
    pausedGoals: goals.filter(g => g.status === "paused").length,
    overallProgress: goals.length > 0 
      ? Math.round(goals.reduce((sum, goal) => sum + goal.progress, 0) / goals.length)
      : 0,
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Target className="h-5 w-5 text-primary" />
        <span className="text-3xl font-bold">{goalTrends.totalGoals}</span>
      </div>
      {showDetails && (
        <p className="text-sm text-muted-foreground mb-2">
          {goalTrends.activeGoals} active • {goalTrends.completedGoals} completed • {goalTrends.pausedGoals} paused
        </p>
      )}
      {showProgress && (
        <>
          <Progress value={goalTrends.overallProgress} className="mt-2" />
          <p className="text-xs text-muted-foreground mt-1">
            {goalTrends.overallProgress}% average progress
          </p>
        </>
      )}
    </div>
  );
}

export function JournalInsightsWidget({ showStreak = true, showDetails = true }: WidgetSettings) {
  const { data: journalStats } = useQuery<JournalStats>({
    queryKey: ["/api/stats/journal"],
  });

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Activity className="h-5 w-5 text-primary" />
        <span className="text-3xl font-bold">{journalStats?.totalEntries || 0}</span>
      </div>
      {showDetails && (
        <p className="text-sm text-muted-foreground">
          {journalStats?.entriesThisMonth || 0} this month
        </p>
      )}
      {showStreak && (
        <div className="mt-2">
          <Badge variant="outline" className="text-xs">
            {journalStats?.writingStreak || 0} day streak
          </Badge>
        </div>
      )}
    </div>
  );
}

export function HabitPerformanceWidget({ showStreak = true, showDetails = true }: WidgetSettings) {
  const localDate = useTodayDate();
  const { data: habitStats } = useQuery<HabitStats>({
    queryKey: ["/api/stats/habits", localDate],
    queryFn: () =>
      fetch(`/api/stats/habits?date=${localDate}`, { credentials: "include", cache: "no-store" }).then(r => r.json()),
  });

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <TrendingUp className="h-5 w-5 text-primary" />
        <span className="text-3xl font-bold">{habitStats?.totalHabits || 0}</span>
      </div>
      {showDetails && (
        <p className="text-sm text-muted-foreground">
          {habitStats?.completedToday || 0} completed today
        </p>
      )}
      {showStreak && (
        <div className="mt-2">
          <Badge variant="outline" className="text-xs">
            {habitStats?.longestStreak || 0} day best streak
          </Badge>
        </div>
      )}
    </div>
  );
}

export function CalendarActivityWidget({ showDetails = true }: WidgetSettings) {
  const { data: calendarStats } = useQuery<CalendarStats>({
    queryKey: ["/api/stats/calendar"],
  });

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Calendar className="h-5 w-5 text-primary" />
        <span className="text-3xl font-bold">{calendarStats?.totalEvents || 0}</span>
      </div>
      {showDetails && (
        <p className="text-sm text-muted-foreground">
          {calendarStats?.upcomingEvents || 0} upcoming events
        </p>
      )}
      <div className="mt-2">
        <Badge variant="outline" className="text-xs">
          {calendarStats?.completedEvents || 0} completed
        </Badge>
      </div>
    </div>
  );
}

export function CategoryPerformanceWidget({ maxItems = 5 }: WidgetSettings) {
  const { data: allGoals = [] } = useQuery<Goal[]>({
    queryKey: ["/api/goals"],
  });
  const goals = allGoals.filter(isGoalVisibleOnMainSurfaces);

  const { data: categoryStats = {} } = useQuery<CategoryStats>({
    queryKey: ["/api/stats/categories"],
  });

  const categoryAnalytics = Object.entries(categoryStats).map(([category, count]) => {
    const categoryGoals = goals.filter(goal => goal.category === category);
    const completed = categoryGoals.filter(goal => goal.status === "completed").length;
    const active = categoryGoals.filter(goal => goal.status === "active").length;
    const avgProgress = active > 0 
      ? Math.round(categoryGoals.filter(goal => goal.status === "active").reduce((sum, goal) => sum + goal.progress, 0) / active)
      : 0;

    return {
      category,
      totalGoals: categoryGoals.length,
      completed,
      active,
      avgProgress,
      completionRate: categoryGoals.length > 0 ? Math.round((completed / categoryGoals.length) * 100) : 0,
    };
  }).slice(0, maxItems);

  return (
    <div className="space-y-4">
      {categoryAnalytics.length > 0 ? (
        categoryAnalytics.map((category) => (
          <div key={category.category} className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{category.category}</span>
              <span className="text-sm text-muted-foreground">
                {category.completed}/{category.totalGoals} completed
              </span>
            </div>
            <Progress value={category.avgProgress} className="h-2" />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{category.avgProgress}% avg progress</span>
              <span>{category.completionRate}% completion rate</span>
            </div>
          </div>
        ))
      ) : (
        <div className="text-center text-muted-foreground py-4">
          No category data available yet
        </div>
      )}
    </div>
  );
}

export function RecentActivityAnalyticsWidget({ maxItems = 8 }: WidgetSettings) {
  const { data: activities = [] } = useQuery<ActivityType[]>({
    queryKey: ["/api/activities"],
  });

  const recentActivities = activities.slice(0, maxItems);

  return (
    <div className="space-y-3">
      {recentActivities.length > 0 ? (
        recentActivities.map((activity) => (
          <div key={activity.id} className="flex items-start gap-3">
            <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm">{activity.description}</p>
              <p className="text-xs text-muted-foreground">
                {new Date(activity.createdAt).toLocaleDateString()} at{" "}
                {new Date(activity.createdAt).toLocaleTimeString([], { 
                  hour: "2-digit", 
                  minute: "2-digit" 
                })}
              </p>
            </div>
          </div>
        ))
      ) : (
        <div className="text-center text-muted-foreground py-4">
          No recent activity
        </div>
      )}
    </div>
  );
}

export function ProductivityInsightsWidget({ metric = "all" }: WidgetSettings) {
  const { data: dashboardStats } = useQuery<DashboardStats>({
    queryKey: ["/api/stats/dashboard"],
  });

  const { data: journalStats } = useQuery<JournalStats>({
    queryKey: ["/api/stats/journal"],
  });

  const localDate = useTodayDate();
  const { data: habitStats } = useQuery<HabitStats>({
    queryKey: ["/api/stats/habits", localDate],
    queryFn: () =>
      fetch(`/api/stats/habits?date=${localDate}`, { credentials: "include", cache: "no-store" }).then(r => r.json()),
  });

  const metrics = [
    { key: "successRate", value: dashboardStats?.successRate || 0, label: "Success Rate", color: "text-blue-600", suffix: "%" },
    { key: "writingStreak", value: journalStats?.writingStreak || 0, label: "Writing Streak", color: "text-green-600", suffix: "" },
    { key: "habitStreak", value: habitStats?.longestStreak || 0, label: "Best Habit Streak", color: "text-purple-600", suffix: "" },
  ];

  const displayMetrics = metric === "all" ? metrics : metrics.filter(m => m.key === metric);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {displayMetrics.map((m) => (
        <div key={m.key} className="text-center p-4 bg-muted/50 rounded-lg">
          <div className={`text-2xl font-bold ${m.color}`}>
            {m.value}{m.suffix}
          </div>
          <div className="text-sm text-muted-foreground">{m.label}</div>
        </div>
      ))}
    </div>
  );
}

export function TasksOverviewWidget({ showDetails = true }: WidgetSettings) {
  const { data: dashboardStats } = useQuery<DashboardStats>({
    queryKey: ["/api/stats/dashboard"],
  });

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <BarChart3 className="h-5 w-5 text-primary" />
        <span className="text-3xl font-bold">{dashboardStats?.activeTasks || 0}</span>
      </div>
      {showDetails && (
        <p className="text-sm text-muted-foreground">
          {dashboardStats?.completedTasks || 0} completed
        </p>
      )}
      <div className="mt-2">
        <Badge variant="outline" className="text-xs">
          {dashboardStats?.successRate || 0}% success rate
        </Badge>
      </div>
    </div>
  );
}

export function MeditationStatsWidget({ showDetails = true }: WidgetSettings) {
  const { data: meditationStats } = useQuery<any>({
    queryKey: ["/api/stats/meditation"],
  });

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Award className="h-5 w-5 text-primary" />
        <span className="text-3xl font-bold">{meditationStats?.totalMinutes || 0}</span>
        <span className="text-sm text-muted-foreground">min</span>
      </div>
      {showDetails && (
        <p className="text-sm text-muted-foreground">
          {meditationStats?.totalSessions || 0} sessions total
        </p>
      )}
      <div className="mt-2">
        <Badge variant="outline" className="text-xs">
          {meditationStats?.sessionsThisWeek || 0} this week
        </Badge>
      </div>
    </div>
  );
}
